import React, { useState, useEffect } from 'react';
import { chatService } from '../../services/chatService';
import { contactService } from '../../services/contactService';
import toast from 'react-hot-toast';
import { Clock, Check, CheckCheck, Edit2, Trash2, MoreVertical, XCircle } from 'lucide-react';

const ContactList = ({ searchTerm, selectedContactId, onContactSelect, onEditContact, onDeleteContact }) => {
  const [conversations, setConversations] = useState([]);
  const [allContacts, setAllContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showMenu, setShowMenu] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (searchTerm) {
      loadAllContacts();
    }
  }, [searchTerm]);

  // Poll for new messages every 3 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      loadConversations();
    }, 3000);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const handleConversationRead = () => {
      loadConversations();
    };
    window.addEventListener('chat:conversation-read', handleConversationRead);
    return () => window.removeEventListener('chat:conversation-read', handleConversationRead);
  }, []);

  const loadData = async () => {
    await Promise.all([loadConversations(), loadAllContacts()]);
  };

  const loadConversations = async () => {
    try {
      const data = await chatService.getConversations();
      setConversations(data || []);
    } catch (error) {
      console.error('Error loading conversations:', error);
      if (conversations.length === 0 && !searchTerm) {
        toast.error('Failed to load conversations');
      }
    } finally {
      setLoading(false);
    }
  };

  const loadAllContacts = async () => {
    try {
      const data = await contactService.getContacts(0, 100, true);
      setAllContacts(data?.content || []);
    } catch (error) {
      console.error('Error loading contacts:', error);
    }
  };

  // Merge conversations and all contacts, prioritizing conversations
  const getMergedContacts = () => {
    if (searchTerm) {
      // When searching, show all matching contacts
      return allContacts.filter(contact =>
        contact.contactName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        contact.phoneNumber?.includes(searchTerm)
      );
    }

    // Merge conversations with all contacts
    const conversationContactIds = new Set(conversations.map(c => c.contactId));
    const contactsWithoutConversations = allContacts.filter(c => !conversationContactIds.has(c.contactId));
    
    // Convert conversations to contact format and merge
    const conversationContacts = conversations.map(conv => ({
      contactId: conv.contactId,
      contactName: conv.contactName,
      phoneNumber: conv.phoneNumber,
      lastMessage: conv.lastMessage,
      lastMessageTime: conv.lastMessageTime,
      unreadCount: conv.unreadCount,
      lastMessageDirection: conv.lastMessageDirection,
      lastMessageStatus: conv.lastMessageStatus,
      isConversation: true
    }));

    return [...conversationContacts, ...contactsWithoutConversations];
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    
    return date.toLocaleDateString();
  };

  const getStatusIcon = (direction, status) => {
    if (direction === 'inbound') return null;

    const normalizedStatus = String(status || '').trim().toLowerCase();

    if (normalizedStatus === 'read' || normalizedStatus === 'delivered') {
      return <CheckCheck className="w-4 h-4 text-primary-600" />;
    }
    if (normalizedStatus === 'sent') {
      return <Check className="w-4 h-4 text-gray-400" />;
    }
    if (normalizedStatus === 'failed') {
      return <XCircle className="w-4 h-4 text-error-500" />;
    }
    return <Clock className="w-4 h-4 text-gray-400" />;
  };

  const mergedContacts = getMergedContacts();

  if (loading && mergedContacts.length === 0) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-gray-500">Loading contacts...</div>
      </div>
    );
  }

  if (mergedContacts.length === 0) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center text-gray-500">
          <p className="text-sm">{searchTerm ? 'No contacts found' : 'No contacts found'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="divide-y divide-gray-100">
      {mergedContacts.map((item) => {
        const contactId = item.contactId;
        const contactName = item.contactName || 'Unknown';
        const lastMessage = item.lastMessage || null;
        const lastMessageTime = item.lastMessageTime || null;
        const unreadCount = item.unreadCount || 0;
        const isSelected = String(selectedContactId) === String(contactId);
        const isMenuOpen = showMenu === contactId;

        return (
          <div
            key={contactId}
            className={`px-4 py-3 hover:bg-gray-50 transition-colors relative ${
              isSelected ? 'bg-primary-50 border-l-4 border-primary-600' : ''
            }`}
          >
            <div className="flex items-start gap-3">
              {/* Avatar */}
              <div 
                onClick={() => onContactSelect(contactId)}
                className="flex-shrink-0 w-12 h-12 rounded-full bg-primary-100 flex items-center justify-center cursor-pointer"
              >
                <span className="text-primary-600 font-semibold text-sm">
                  {contactName.charAt(0).toUpperCase()}
                </span>
              </div>

              {/* Content */}
              <div 
                onClick={() => onContactSelect(contactId)}
                className="flex-1 min-w-0 cursor-pointer"
              >
                <div className="flex items-center justify-between mb-1">
                  <h3 className="text-sm font-semibold text-gray-900 truncate">
                    {contactName}
                  </h3>
                  {lastMessageTime && (
                    <span className="text-xs text-gray-500 ml-2 flex-shrink-0">
                      {formatTime(lastMessageTime)}
                    </span>
                  )}
                </div>

                {lastMessage && (
                  <div className="flex items-center gap-2">
                    <p className="text-sm text-gray-600 truncate flex-1">
                      {lastMessage}
                    </p>
                    {getStatusIcon(item.lastMessageDirection, item.lastMessageStatus)}
                  </div>
                )}

                {!lastMessage && (
                  <p className="text-sm text-gray-500 truncate">
                    {item.phoneNumber}
                  </p>
                )}

                {unreadCount > 0 && (
                  <div className="mt-1">
                    <span className="inline-flex items-center justify-center w-5 h-5 text-xs font-medium text-white bg-primary-600 rounded-full">
                      {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                  </div>
                )}
              </div>

              {/* Actions Menu */}
              {(onEditContact || onDeleteContact) && (
                <div className="relative">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowMenu(isMenuOpen ? null : contactId);
                    }}
                    className="p-1 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors"
                  >
                    <MoreVertical className="w-4 h-4" />
                  </button>
                  
                  {isMenuOpen && (
                    <>
                      <div 
                        className="fixed inset-0 z-10" 
                        onClick={() => setShowMenu(null)}
                      />
                      <div className="absolute right-0 mt-1 w-32 bg-white rounded-lg shadow-lg border border-gray-200 z-20">
                        {onEditContact && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setShowMenu(null);
                              onEditContact(item);
                            }}
                            className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                          >
                            <Edit2 className="w-4 h-4" />
                            Edit
                          </button>
                        )}
                        {onDeleteContact && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setShowMenu(null);
                              onDeleteContact(item);
                            }}
                            className="w-full px-3 py-2 text-left text-sm text-error-600 hover:bg-error-50 flex items-center gap-2"
                          >
                            <Trash2 className="w-4 h-4" />
                            Delete
                          </button>
                        )}
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default ContactList;

